import { EStreamTradeDTO } from '../../types/estream/eStreamTrade';
import { InstrumentStoreItem, InstrumentStoreItemOrderBook } from '../../types/estream/instrumentStoreItem';
import { isNumber } from '../../utils';
import { isDefined } from '../../utils/objectUtils';

const bookSize = 10;
const bookIndexes = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

const getBookOrEmptyBook = (asksOrBids: [number, number][] | undefined, size = 10): [number, number][] => {
  if (isDefined(asksOrBids)) {
    // Deep clone the nested structure, so we don't accidentally mutate old values.
    return asksOrBids.map(([price, vol]): [number, number] => [price, vol]);
  }
  const res: Array<[number, number]> = [];
  for (let i = 0; i < size; i++) {
    res.push([0, 0]);
  }
  return res;
};

export const mapOrderBook = (
  item: EStreamTradeDTO,
  previousValue?: InstrumentStoreItem,
): InstrumentStoreItemOrderBook => {
  const book: Partial<InstrumentStoreItem['book']> = {
    asks: getBookOrEmptyBook(previousValue?.book?.asks, bookSize),
    bids: getBookOrEmptyBook(previousValue?.book?.bids, bookSize),
  };

  // Not a book update, return the previous value
  // isDefined won't work as null is a valid value for the price and volume (empty row)
  if (
    item.data &&
    item.data.BEST_ASK1 === undefined &&
    item.data.BEST_BID1 === undefined &&
    item.data.BEST_ASIZ1 === undefined &&
    item.data.BEST_BSIZ1 === undefined &&
    item.data.BID === undefined &&
    item.data.ASK === undefined
  ) {
    return book;
  }

  if (item.data && book.asks && book.bids) {
    for (const index of bookIndexes) {
      const askPrice = item.data[`BEST_ASK${index}`];
      const askVol = index === 10 ? item.data[`BEST_ASZ10`] : item.data[`BEST_ASIZ${index}`];
      const bidPrice = item.data[`BEST_BID${index}`];
      const bidVol = index === 10 ? item.data[`BEST_BSZ10`] : item.data[`BEST_BSIZ${index}`];

      // isDefined won't work as null is a valid value for the price and volume (empty row)
      if (askPrice !== undefined) {
        book.asks[index - 1][0] = isNumber(askPrice) && !Number.isNaN(askPrice) ? askPrice : 0;
      }
      if (askVol !== undefined) {
        book.asks[index - 1][1] = isNumber(askVol) && !Number.isNaN(askVol) ? askVol : 0;
      }
      if (bidPrice !== undefined) {
        book.bids[index - 1][0] = isNumber(bidPrice) && !Number.isNaN(bidPrice) ? bidPrice : 0;
      }
      if (bidVol !== undefined) {
        book.bids[index - 1][1] = isNumber(bidVol) && !Number.isNaN(bidVol) ? bidVol : 0;
      }
    }
  }

  if (item.data) {
    book.isBidMarketPrice = item.data.BID === 'M' || item.data.BEST_BID1 === 'M';
    book.isAskMarketPrice = item.data.ASK === 'M' || item.data.BEST_ASK1 === 'M';
  }

  return book;
};
